import React from "react";
import Link from "next/link";
import { getAvatarUrl } from "@/utils/avatar/avatarHelpers";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";

interface UserAvatarProps {
  user: {
    id: string;
    avatar_url?: string | null;
    username?: string | null;
    slug?: string | null;
  };
  size?: "sm" | "md" | "lg" | "xl";
  linkToProfile?: boolean;
  isLoading?: boolean;
}

const sizeClasses = {
  sm: "size-6",
  md: "size-8",
  lg: "size-10",
  xl: "size-16",
} as const;

export const UserAvatar = React.memo(function UserAvatar({
  user,
  size = "md",
  linkToProfile = true,
  isLoading = false,
}: UserAvatarProps) {
  const avatarUrl = getAvatarUrl(user.avatar_url);
  const userSlug = user.slug || user.id;
  const username = user.username || "User";
  const initials = username.charAt(0).toUpperCase();

  const avatarComponent = (
    <Avatar className={`${sizeClasses[size]} ring-background ring-2`}>
      {isLoading ? (
        <Skeleton className="h-full w-full rounded-full" />
      ) : (
        <>
          {avatarUrl && (
            <AvatarImage
              src={avatarUrl}
              alt={username}
              className="object-cover object-center"
            />
          )}
          <AvatarFallback>{initials}</AvatarFallback>
        </>
      )}
    </Avatar>
  );

  if (linkToProfile) {
    return <Link href={`/users/${userSlug}`}>{avatarComponent}</Link>;
  }

  return avatarComponent;
});
